import React from 'react';
import { format, isSameDay, isWeekend } from 'date-fns';
import { useDroppable } from '@dnd-kit/core';
import { formatCustomDate } from '../../../utils/dateFormatters';
import { DraggableEvent } from './DraggableEvent';
import { OccurrenceDraggableEvent } from './OccurrenceDraggableEvent';
import { HoverCard } from './HoverCard';
import { ArrowLeftIcon } from './ArrowLeftIcon';
import { ArrowRightIcon } from './ArrowRightIcon';
import { CalendarEvent, EventColorSet, EventsByDate } from 'pages/Agenda/utils';
import { useWeekView } from '../../../hooks/useWeekView';
import {
  DayViewContainer,
  DayViewHeader,
  DayViewDateInfo,
  DayViewTodayBadge,
  DayViewNavButton,
  DayViewBody,
  DayLabel,
  ExpectedHours,
} from '../styled';

interface DayViewProps {
  currentDate: Date;
  events: EventsByDate;
  horasPrevistasByDate: Record<string, string>;
  isCompletedView: boolean;
  onCardClick: (event: CalendarEvent, colorSet: EventColorSet) => void;
  onNavigate: (direction: 'prev' | 'next') => void;
}

export const DayView: React.FC<DayViewProps> = ({
  currentDate,
  events,
  horasPrevistasByDate,
  isCompletedView,
  onCardClick,
  onNavigate,
}) => {
  const { hoveredCard, setHoveredCard, isDragging, getColorSet } = useWeekView();
  const dateKey = format(currentDate, 'yyyy-MM-dd');
  const dayEvents = events[dateKey] || [];
  const isToday = isSameDay(currentDate, new Date());
  const isWeekendDay = isWeekend(currentDate);

  const { setNodeRef, isOver } = useDroppable({ id: dateKey });

  return (
    <>
      <DayViewContainer ref={setNodeRef} isWeekend={isWeekendDay} isDropTarget={isOver}>
        <DayViewHeader>
          <DayViewNavButton side="left" type="button" onClick={() => onNavigate('prev')} aria-label="Voltar um dia">
            <ArrowLeftIcon />
          </DayViewNavButton>
          <DayViewDateInfo>
            {isToday && <DayViewTodayBadge>Hoje</DayViewTodayBadge>}
            <DayLabel isHighlighted={isToday}>{formatCustomDate(currentDate)}</DayLabel>
            <ExpectedHours>{horasPrevistasByDate[dateKey] ?? '00:00h'} previstas</ExpectedHours>
          </DayViewDateInfo>
          <DayViewNavButton side="right" type="button" onClick={() => onNavigate('next')} aria-label="Avançar um dia">
            <ArrowRightIcon />
          </DayViewNavButton>
        </DayViewHeader>
        <DayViewBody>
          {dayEvents.map(event =>
            event.eventKind === 'ocorrencia' ? (
              <OccurrenceDraggableEvent
                key={event.id}
                event={event}
                onHover={setHoveredCard}
                onLeave={() => setHoveredCard(null)}
                onCardClick={onCardClick}
                isDragging={isDragging}
                colorSet={getColorSet(event)}
                isCompletedView={isCompletedView}
              />
            ) : (
              <DraggableEvent
                key={event.id}
                event={event}
                onHover={setHoveredCard}
                onLeave={() => setHoveredCard(null)}
                onCardClick={onCardClick}
                isDragging={isDragging}
                colorSet={getColorSet(event)}
                isCompletedView={isCompletedView}
              />
            ),
          )}
        </DayViewBody>
      </DayViewContainer>
      {hoveredCard && <HoverCard {...hoveredCard} isCompletedView={isCompletedView} />}
    </>
  );
};
